import { calculateTotal } from '@calculations/price/utilities/total';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { Decimal } from '@snap-types/types/decimal';
import { WritableQuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../../utilities/action-guards';
import { OnGrossMarginChangeInput } from '../types';
import { applyQuotePriceRecalcResult } from '../utilities/apply-quote-price-recalc-result';
import { buildStrategyCalculations } from '../utilities/build-strategy-calculations';

export function onPartGrossMarginChange(
  state: WritableQuoteBuilderStateType,
  params: {
    quoteLineUuid: string;
    quotePriceUuid: string;
    grossMargin: Decimal;
    priceStrategyConfigs: PriceStrategyConfigWithRelations[];
  },
) {
  const quoteLine = getQuoteLineOrThrow(state, params.quoteLineUuid);
  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );
  const { priceStrategy } = quotePrice;

  const inputs = {
    state,
    quoteLine,
    quotePrice,
    grossMargin: params.grossMargin,
    priceStrategyConfigs: params.priceStrategyConfigs,
  } satisfies OnGrossMarginChangeInput;

  const calculation = buildStrategyCalculations('onGrossMarginChange')[
    priceStrategy
  ];

  if (!calculation) {
    console.error(
      'No gross margin calculation function found for price strategy',
      priceStrategy,
    );
    return;
  }

  const output = calculation(inputs);

  if (output.status === 'failure') {
    // @TODO(NT): Our store should be integrated with toast notifications
    console.error('Failed to calculate price', output.reason);
    return;
  }

  applyQuotePriceRecalcResult(state, {
    quoteLineUuid: params.quoteLineUuid,
    quotePriceUuid: params.quotePriceUuid,
    quotePrice,
    patch: {
      grossMargin: params.grossMargin,
      calculatedGrossMargin: null,
      userGrossMargin: params.grossMargin,
    },
  });

  if (output.status === 'success') {
    const unitPrice = output.unitPrice;

    applyQuotePriceRecalcResult(state, {
      quoteLineUuid: params.quoteLineUuid,
      quotePriceUuid: params.quotePriceUuid,
      quotePrice,
      patch: {
        unitPrice,
        calculatedUnitPrice: unitPrice,
        userUnitPrice: null,
        totalPrice: calculateTotal(
          unitPrice,
          quotePrice.quantity,
          SnapRoundingOptionsCustom(state),
        ),
        contributionMargin: output.contributionMargin,
        suggestedPriceStatus: SnapSuggestionStatus.REJECTED,
      },
      rawFieldsToClear: ['unitPrice', 'contributionMargin'],
    });
  }

  quoteLine.automatable = false;

  if (quotePrice.suggestedPriceStatus === SnapSuggestionStatus.REJECTED) {
    quoteLine.suggestedPricesStatus = SnapSuggestionStatus.REJECTED;
  }
}
